import React, { useRef, useCallback, useEffect, useMemo, useState } from 'react'
import AutoSizer from 'react-virtualized-auto-sizer'
import { List, CellMeasurer, CellMeasurerCache } from 'react-virtualized'
import {
  MoreVertical,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  FileDown
} from 'lucide-react'

const SkeletonRow = () => (
  <div className='flex border-b border-gray-200 p-2 animate-pulse'>
    <div
      className='flex-1 px-2 py-2 bg-gray-200 rounded-md mr-2'
      style={{ minHeight: 40 }}
    />
  </div>
)

const LoadingMoreRow = () => (
  <div className='flex justify-center items-center p-4 text-gray-500'>
    Loading more...
  </div>
)

const NoDataRow = () => (
  <div className='px-2 py-4 text-center text-gray-500'>
    No CSS Details available
  </div>
)

const ExportMenu = React.memo(({ onExportClick, sortedRecords }) => {
  const [open, setOpen] = useState(false)
  const menuRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = e => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div className='relative inline-block text-left' ref={menuRef}>
      <button
        onClick={() => setOpen(o => !o)}
        className={`p-2 rounded-full hover:bg-purple-100 focus:outline-none 
                    focus-visible:ring-2 focus-visible:ring-purple-500 transition-all duration-200`}
      >
        <MoreVertical
          size={20}
          className={`text-white-800 hover:text-purple-600 transition-colors`}
        />
      </button>
      {open && (
        <div
          className={`absolute right-0 mt-2 w-48 origin-top-right 
                      rounded-2xl border border-gray-200 bg-white backdrop-blur-sm shadow-xl 
                      z-50 transition-all duration-200 ease-out animate-fadeScale`}
        >
          <button
            onClick={() => {
              setOpen(false)
              onExportClick(sortedRecords)
            }}
            className='flex items-center w-full gap-2 px-4 py-2.5 text-sm text-gray-700
                       hover:bg-purple-50 hover:text-purple-700 active:bg-purple-100 
                       rounded-2xl transition-all duration-150'
          >
            <FileDown size={16} className='text-purple-600' />
            Export to CSV
          </button>
        </div>
      )}
    </div>
  )
})

const SortIcon = ({ sortKey, currentSort }) => {
  if (currentSort.key !== sortKey) {
    return <ChevronsUpDown size={14} className='text-gray-400' />
  }
  return currentSort.direction === 'asc' ? (
    <ChevronUp size={14} className='text-white' />
  ) : (
    <ChevronDown size={14} className='text-white' />
  )
}

const ServiceRecordsTable = ({
  records,
  isInitialLoading,
  isLoadingMore,
  selectedRecord,
  setSelectedRecord,
  fetchNextBatch,
  hasMore,
  setCaregiverBatchNumber,
  setHasMoreCaregivers,
  handleExport
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' })
  const sortedRecords = useMemo(() => {
    if (!sortConfig.key) return records
    return [...records].sort((a, b) => {
      let aVal = a[sortConfig.key]
      let bVal = b[sortConfig.key]
      if (
        ['FUTURE_HOURS_NO_CAREGIVER', 'POTENTIAL_UNDER_SERVED'].includes(
          sortConfig.key
        )
      ) {
        aVal = Number(aVal) || 0
        bVal = Number(bVal) || 0
      } else {
        aVal = String(aVal || '').toLowerCase()
        bVal = String(bVal || '').toLowerCase()
      }
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1
      return 0
    })
  }, [records, sortConfig])

  const sortedRecordsRef = useRef(sortedRecords)
  useEffect(() => {
    sortedRecordsRef.current = sortedRecords
  }, [sortedRecords])

  const cache = useRef(
    new CellMeasurerCache({
      defaultHeight: 50,
      fixedWidth: true,
      keyMapper: rowIndex =>
        sortedRecordsRef.current[rowIndex]?.CLIENT_NUMBER ?? `row-${rowIndex}`
    })
  )

  const listRef = useRef()

  useEffect(() => {
    if (listRef.current) {
      listRef.current.recomputeRowHeights()
    }
  }, [sortedRecords])

  const handleToggleLogic = record => {
    setCaregiverBatchNumber(1)
    setHasMoreCaregivers(true)
    setSelectedRecord(prev => {
      const areEqual = selectedRecord && record &&
                Object.keys(selectedRecord).every(
                  key => selectedRecord[key] === record[key]
                )
      if (areEqual) return null
      return record
    })
  }

  const handleSort = key => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }))
  }

  const { totalFutureHrs, totalUnderserved } = useMemo(() => {
    return sortedRecords.reduce(
      (acc, rec) => {
        acc.totalFutureHrs += Number(rec.FUTURE_HOURS_NO_CAREGIVER) || 0
        acc.totalUnderserved += Number(rec.POTENTIAL_UNDER_SERVED) || 0
        return acc
      },
      { totalFutureHrs: 0, totalUnderserved: 0 }
    )
  }, [sortedRecords])

  const footerText = useMemo(() => {
    return hasMore
      ? `Showing ${sortedRecords.length} records (partial)`
      : `Showing all ${sortedRecords.length} records`
  }, [sortedRecords.length, hasMore])

  const Row = ({ index, key, parent, style }) => (
    <CellMeasurer
      key={key}
      cache={cache.current}
      parent={parent}
      columnIndex={0}
      rowIndex={index}
    >
      {({ registerChild }) => {
        if (isInitialLoading)
          return (
            <div ref={registerChild} style={style}>
              <SkeletonRow />
            </div>
          )
        if (index >= sortedRecords.length)
          return (
            <div ref={registerChild} style={style}>
              <LoadingMoreRow />
            </div>
          )

        const record = sortedRecords[index]
        return (
          <div
            ref={registerChild}
            style={style}
            className={`flex border-b border-gray-200 cursor-pointer p-2 ${
                selectedRecord && record &&
                Object.keys(selectedRecord).every(
                  key => selectedRecord[key] === record[key]
                )
                ? 'bg-indigo-50'
                : 'hover:bg-gray-50'
            }`}
            onClick={() => handleToggleLogic(record)}
          >
            <div className='flex-1 px-2' title={record.CSS_NAME}>
              {record.CSS_NAME}
            </div>
            <div className='flex-1 px-2' title={record.CLIENT_NUMBER}>
              {record.CLIENT_NUMBER}
            </div>
            <div className='flex-1 px-2' title={record.CLIENT_NAME}>
              {record.CLIENT_NAME}
            </div>
            <div className='flex-1 px-2' title={record.CONTRACT_NAME}>
              {record.CONTRACT_NAME}
            </div>
            <div className='flex-1 px-2' title={record.SERVICE_NAME}>
              {record.SERVICE_NAME}
            </div>
            <div
              className='flex-1 px-2 text-right'
              title={
                record.FUTURE_HOURS_NO_CAREGIVER != null
                  ? Number(record.FUTURE_HOURS_NO_CAREGIVER).toFixed(2)
                  : '-'
              }
            >
              {record.FUTURE_HOURS_NO_CAREGIVER != null
                ? Number(record.FUTURE_HOURS_NO_CAREGIVER).toFixed(2)
                : '-'}
            </div>
            <div
              className='flex-1 px-2 text-right'
              title={
                record.POTENTIAL_UNDER_SERVED != null
                  ? Number(record.POTENTIAL_UNDER_SERVED).toFixed(2)
                  : '-'
              }
            >
              {record.POTENTIAL_UNDER_SERVED != null
                ? Number(record.POTENTIAL_UNDER_SERVED).toFixed(2)
                : '-'}
            </div>
          </div>
        )
      }}
    </CellMeasurer>
  )

  const handleScroll = useCallback(
    ({ clientHeight, scrollHeight, scrollTop }) => {
      if (
        scrollHeight - scrollTop - clientHeight < 500 &&
        hasMore &&
        !isLoadingMore &&
        !isInitialLoading
      ) {
        fetchNextBatch()
      }
    },
    [hasMore, isLoadingMore, isInitialLoading]
  )

  const rowCount = isInitialLoading
    ? 10
    : sortedRecords.length + (isLoadingMore ? 1 : 0)

  return (
    <div className='container mx-auto bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-5 relative'>
      <div className='flex items-center justify-between bg-purple-700 text-white font-medium text-xs uppercase sticky top-0 z-20'>
        <div className='flex flex-1'>
          <div
            className='flex-1 px-2 py-2 cursor-pointer hover:bg-purple-600 flex items-center justify-between'
            onClick={() => handleSort('CSS_NAME')}
          >
            <span>CSS</span>
            <SortIcon sortKey='CSS_NAME' currentSort={sortConfig} />
          </div>
          <div
            className='flex-1 px-2 py-2 cursor-pointer hover:bg-purple-600 flex items-center justify-between'
            onClick={() => handleSort('CLIENT_NUMBER')}
          >
            <span>CLIENT NUMBER</span>
            <SortIcon sortKey='CLIENT_NUMBER' currentSort={sortConfig} />
          </div>
          <div
            className='flex-1 px-2 py-2 cursor-pointer hover:bg-purple-600 flex items-center justify-between'
            onClick={() => handleSort('CLIENT_NAME')}
          >
            <span>CLIENT</span>
            <SortIcon sortKey='CLIENT_NAME' currentSort={sortConfig} />
          </div>
          <div
            className='flex-1 px-2 py-2 cursor-pointer hover:bg-purple-600 flex items-center justify-between'
            onClick={() => handleSort('CONTRACT_NAME')}
          >
            <span>CONTRACT NAME</span>
            <SortIcon sortKey='CONTRACT_NAME' currentSort={sortConfig} />
          </div>
          <div
            className='flex-1 px-2 py-2 cursor-pointer hover:bg-purple-600 flex items-center justify-between'
            onClick={() => handleSort('SERVICE_NAME')}
          >
            <span>SERVICE NAME</span>
            <SortIcon sortKey='SERVICE_NAME' currentSort={sortConfig} />
          </div>
          <div
            className='flex-1 px-2 py-2 bg-teal-500 cursor-pointer hover:bg-teal-400 text-right flex items-center justify-between'
            onClick={() => handleSort('FUTURE_HOURS_NO_CAREGIVER')}
          >
            <span>FUTURE HRS/VISITS NO CAREGIVER</span>
            <SortIcon
              sortKey='FUTURE_HOURS_NO_CAREGIVER'
              currentSort={sortConfig}
            />
          </div>
          <div
            className='flex-1 px-2 py-2 bg-orange-400 cursor-pointer hover:bg-orange-300 text-right flex items-center justify-between'
            onClick={() => handleSort('POTENTIAL_UNDER_SERVED')}
          >
            <span>POTENTIAL UNDER SERVED</span>
            <SortIcon
              sortKey='POTENTIAL_UNDER_SERVED'
              currentSort={sortConfig}
            />
          </div>
        </div>
        <ExportMenu
          onExportClick={handleExport}
          sortedRecords={sortedRecords}
        />
      </div>

      <div style={{ height: 400 }}>
        {!isInitialLoading && sortedRecords.length === 0 ? (
          <NoDataRow />
        ) : (
          <AutoSizer>
            {({ width, height }) => (
              <List
                ref={listRef}
                width={width}
                height={height}
                rowCount={rowCount}
                rowHeight={cache.current.rowHeight}
                deferredMeasurementCache={cache.current}
                rowRenderer={Row}
                overscanRowCount={10}
                onScroll={handleScroll}
              />
            )}
          </AutoSizer>
        )}
      </div>

      <div className='flex bg-gray-100 font-semibold text-sm border-t border-gray-200 sticky bottom-0'>
        <div className='flex-1 px-2 py-2 text-gray-700'>
          TOTAL
          <p className='text-xs font-normal text-gray-500'>{footerText}</p>
        </div>
        <div className='flex-1 px-2 py-2'></div>
        <div className='flex-1 px-2 py-2'></div>
        <div className='flex-1 px-2 py-2'></div>
        <div className='flex-1 px-2 py-2'></div>
        <div className='flex-1 px-2 py-2 text-right text-teal-700'>
          {totalFutureHrs.toFixed(2)}
        </div>
        <div className='flex-1 px-2 py-2 text-right text-orange-700'>
          {totalUnderserved.toFixed(2)}
        </div>
      </div>
    </div>
  )
}

export default ServiceRecordsTable